// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ViewRouter } from "./router";
import { prefersReducedMotion } from "./device";

interface ToTopRegistration {
  pane: HTMLElement;
  onScroll: () => void;
}

interface ToTopPress {
  pointerId: number;
  startX: number;
  startY: number;
  longPressed: boolean;
}

const TO_TOP_LONG_PRESS_MS = 650;
const TO_TOP_PRESS_MOVE_TOLERANCE_PX = 12;

/**
 * Manages the single global scroll-to-top button and delegates it to
 * registered panes (views). The button element must be in the DOM with
 * `data-sb-scroll-top-global` attribute. It's visibility is controlled
 * by CSS; it should be visible only if it has `data-sb-visible = "1"`
 */
export class SharedControls {
  private readonly toTopScrollThreshold = 120;
  private readonly toTopButton: HTMLButtonElement | null;
  private readonly toTopRegistrations = new Map<string, ToTopRegistration>();
  private readonly router: ViewRouter;
  private currentView: string;
  private toTopPress: ToTopPress | null = null;
  private toTopLongPressTimer: number | null = null;
  private suppressToTopClick = false;

  constructor(opts: { router: ViewRouter; initialView: string }) {
    this.toTopButton = document.querySelector<HTMLButtonElement>("[data-sb-scroll-top-global]");
    this.router = opts.router;
    this.currentView = opts.initialView || "home";

    this.toTopButton?.addEventListener("pointerdown", this.handleToTopPointerDown);
    this.toTopButton?.addEventListener("pointermove", this.handleToTopPointerMove);
    this.toTopButton?.addEventListener("pointerup", this.handleToTopPointerUp);
    this.toTopButton?.addEventListener("pointercancel", this.handleToTopPointerCancel);
    this.toTopButton?.addEventListener("blur", this.resetToTopPress);
    this.toTopButton?.addEventListener("click", this.handleToTopButtonClick);
    this.router.onViewChange(this.handleViewChange);

    this.updateToTopButtonVisibility();
  }

  /** Register (or re-register) a pane for a given view. Cleans old listeners. */
  registerForToTopButton(view: string, pane: HTMLElement): void {
    if (!this.toTopButton) return;
    if (view !== "home" && view !== "search" && view !== "index" && view !== "song") return;

    const prev = this.toTopRegistrations.get(view);
    if (prev) {
      prev.pane.removeEventListener("scroll", prev.onScroll);
    }

    const onScroll = () => {
      if (this.currentView !== view) return;
      this.updateToTopButtonVisibility();
    };

    pane.addEventListener("scroll", onScroll, { passive: true });
    this.toTopRegistrations.set(view, { pane, onScroll });

    // Refresh visibility in case this is the active view.
    this.updateToTopButtonVisibility();
  }

  private handleViewChange = (view: string): void => {
    this.currentView = view || "home";
    this.updateToTopButtonVisibility();
  };

  private handleToTopButtonClick = (event: MouseEvent): void => {
    if (this.suppressToTopClick) {
      this.suppressToTopClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.scrollToTop();
  };

  private handleToTopPointerDown = (event: PointerEvent): void => {
    if (!event.isPrimary || event.button !== 0) return;

    this.resetToTopPress();
    this.toTopPress = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      longPressed: false,
    };
    try {
      this.toTopButton?.setPointerCapture(event.pointerId);
    } catch {}

    this.toTopLongPressTimer = window.setTimeout(() => {
      const press = this.toTopPress;
      if (!press || press.pointerId !== event.pointerId || press.longPressed) return;
      press.longPressed = true;
      this.suppressToTopClick = true;
      this.scrollToTop();
    }, TO_TOP_LONG_PRESS_MS);
  };

  private handleToTopPointerMove = (event: PointerEvent): void => {
    const press = this.toTopPress;
    if (!press || press.pointerId !== event.pointerId || press.longPressed) return;
    if (
      Math.hypot(event.clientX - press.startX, event.clientY - press.startY) >
      TO_TOP_PRESS_MOVE_TOLERANCE_PX
    ) {
      this.resetToTopPress();
    }
  };

  private handleToTopPointerUp = (event: PointerEvent): void => {
    const press = this.toTopPress;
    if (!press || press.pointerId !== event.pointerId) return;

    this.clearToTopLongPressTimer();
    this.releaseToTopPointerCapture(event.pointerId);
    this.toTopPress = null;
    if (press.longPressed) {
      window.setTimeout(() => {
        this.suppressToTopClick = false;
      }, 0);
    }
  };

  private handleToTopPointerCancel = (event: PointerEvent): void => {
    if (this.toTopPress?.pointerId !== event.pointerId) return;
    this.resetToTopPress();
  };

  private resetToTopPress = (): void => {
    this.clearToTopLongPressTimer();
    this.releaseToTopPointerCapture();
    this.toTopPress = null;
    this.suppressToTopClick = false;
  };

  private clearToTopLongPressTimer(): void {
    if (this.toTopLongPressTimer !== null) {
      window.clearTimeout(this.toTopLongPressTimer);
      this.toTopLongPressTimer = null;
    }
  }

  private releaseToTopPointerCapture(pointerId = this.toTopPress?.pointerId): void {
    if (pointerId === undefined) return;
    try {
      this.toTopButton?.releasePointerCapture(pointerId);
    } catch {}
  }

  private scrollToTop(): void {
    const reg = this.toTopRegistrations.get(this.currentView);
    if (!reg) return;
    reg.pane.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "instant" : "smooth" });
  }

  private updateToTopButtonVisibility(): void {
    if (!this.toTopButton) return;

    const reg = this.toTopRegistrations.get(this.currentView);
    if (!reg) {
      this.hideToTopButton();
      return;
    }

    const visible = reg.pane.scrollTop > this.toTopScrollThreshold;
    if (visible) this.showToTopButton();
    else this.hideToTopButton();
  }

  private showToTopButton(): void {
    if (!this.toTopButton) return;
    this.toTopButton.dataset.sbVisible = "1";
  }

  private hideToTopButton(): void {
    if (!this.toTopButton) return;
    delete this.toTopButton.dataset.sbVisible;
  }
}
